<template>
    <div class="wizard-forms">
        <div class="row">
            <div class="col-md-10 col-md-offset-1">
                <h2>{{ $t('dynamicess.wizard.battery') }}</h2>
                <p>
                    {{
                        $t('dynamicess.batteryDescription', {
                            i18nFallback:
                                'Dynamic ESS needs to know the limitations and the costs of the battery system to generate an optimised schedule for its usage.',
                        })
                    }}
                </p>
                <div class="row">
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            v-model="batteryEnergyCapacity"
                            :required="true"
                            :data-indicator="$t('dynamicess.kwh', { i18nFallback: 'kWh' })"
                            :title="
                                $t('dynamicess.batteryCapacity', {
                                    i18nFallback: 'Battery capacity',
                                })
                            "
                            :checks="[NOT_NEGATIVE_NUMBER, FIELD_IS_KWH, CANT_BE_ZERO]"
                            :tooltip="
                                $t('dynamicess.tooltipBatteryCapacity', {
                                    i18nFallback:
                                        'Maximum amount of energy that can be stored in the battery',
                                })
                            "
                        ></vrm-number-field>
                    </div>
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            v-model="maximumDischargePower"
                            :required="true"
                            :checks="[NOT_NEGATIVE_NUMBER, CANT_BE_ZERO]"
                            :data-indicator="$t('ess.kw', { i18nFallback: 'kW' })"
                            :title="
                                $t('ess.max_battery_discharge_power', {
                                    i18nFallback: 'Maximum discharge power',
                                })
                            "
                            :tooltip="
                                $t('dynamicess.tooltipDischargePower', {
                                    i18nFallback:
                                        'Maximum allowed discharge rate (in kWs) of the battery',
                                })
                            "
                        ></vrm-number-field>
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            :model="maximumChargePower"
                            :required="true"
                            :data-indicator="$t('ess.kw', { i18nFallback: 'kW' })"
                            :title="
                                $t('ess.max_charge_power', {
                                    i18nFallback: 'Maximum charge power',
                                })
                            "
                            :checks="[NOT_NEGATIVE_NUMBER, CANT_BE_ZERO]"
                            :tooltip="
                                $t('dynamicess.tooltipMaxChargePower', {
                                    i18nFallback:
                                        'Maximum allowed charge rate (in kWs) of the battery',
                                })
                            "
                            @input="maximumChargePower = $event"
                        ></vrm-number-field>
                    </div>
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            :model="totalEstimatedBatteryCycles"
                            :title="
                                $t('dynamicess.totalEstimatedBatteryCycles', {
                                    i18nFallback: 'Total estimated battery cycles',
                                })
                            "
                            :data-indicator="' '"
                            :checks="[NOT_NEGATIVE_NUMBER, CANT_BE_ZERO]"
                            @input="totalEstimatedBatteryCycles = $event"
                        ></vrm-number-field>
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            :model="batteryPrice"
                            :data-indicator="currency.sign"
                            :title="
                                $t('dynamicess.batteryPrice', {
                                    i18nFallback: 'Battery price',
                                })
                            "
                            :checks="[NOT_NEGATIVE_NUMBER]"
                            @input="batteryPrice = $event"
                        ></vrm-number-field>
                    </div>
                    <div class="col-xs-12 col-sm-6">
                        <vrm-number-field
                            ref="batteryCostsRef"
                            :model="batteryCosts"
                            :checks="[NOT_NEGATIVE_NUMBER, BATTERY_COST_TOO_HIGH]"
                            :required="true"
                            :data-indicator="
                                currency.sign +
                                    '/' +
                                    $t('dynamicess.kwh', {
                                        i18nFallback: 'kWh',
                                    })
                            "
                            :title="
                                $t('ess.battery_costs', {
                                    i18nFallback: 'Battery costs',
                                })
                            "
                            :tooltip="
                                $t('dynamicess.tooltipBatteryCosts_2', {
                                    i18nFallback:
                                        'You can calculate this value using this formula: price of battery / (amount of battery cycles * capacity of battery)',
                                })
                            "
                            @input="
                                $event => {
                                    batteryCosts = $event;
                                    batteryCostChanged = true;
                                }
                            "
                        ></vrm-number-field>
                    </div>
                </div>
                <br />
                <div :class="{ 'battery-balancing-disabled': !isPeriodicFullChargeOn }">
                    <h4>
                        {{
                            $t('dynamicess.batteryBalancing', {
                                i18nFallback: 'Battery balancing',
                            })
                        }}
                    </h4>
                    <p>
                        {{
                            $t('dynamicess.batteryBalancingDescription', {
                                i18nFallback:
                                    'To optimise battery life, it is recommended to regularly charge fully to 100% for a few hours. Additionally, setting a regular interval (like every 14 days) for full charges, known as the full charge interval, ensures consistent performance and longevity of the battery over time.',
                            })
                        }}
                    </p>
                    <div class="dynamic-ess--subsection-label">
                        {{
                            $t('dynamicess.fullChargeInterval', {
                                i18nFallback: 'Full charge interval',
                            })
                        }}
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-md-10">
                            <vrm-time-counter
                                v-model="periodicFullChargeInterval"
                                :minimum="MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL"
                                :default="DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL"
                                :disabled="!isPeriodicFullChargeOn"
                                :required="isPeriodicFullChargeOn"
                                :unit="$t('dynamicess.days', { i18nFallback: 'days' })"
                                :title="
                                    $t('dynamicess.fullChargeIntervalDescription', {
                                        i18nFallback:
                                            'Please set the number of days between each battery balancing cycle',
                                    })
                                "
                            />
                        </div>
                    </div>
                </div>
                <div class="vrm-info-box--flex vrm-info-box--flex--row">
                    <span
                        v-html="
                            $t(`dynamicess.toggleBatteryBalancing`, {
                                i18nFallback: `Toggle the battery balancing feature`,
                            })
                        "
                    ></span>
                    <vrm-toggle v-model="isPeriodicFullChargeOn" />
                </div>
                <template v-if="batteryFlowRestrictionsSupported">
                    <br />
                    <h4>
                        {{
                            $t('dynamicess.batteryFlowRestriction', {
                                i18nFallback: 'Charge or discharge restriction',
                            })
                        }}
                    </h4>
                    <p>
                        {{
                            $t('dynamicess.batteryFlowRestrictionDescription', {
                                i18nFallback:
                                    'In some jurisdictions or under certain contracts you are not allowed to sell energy back to the grid that you bought before. To prevent this, you can disable charging or discharging the battery from / to the grid.',
                            })
                        }}
                    </p>
                    <VrmOptionField
                        v-model="batteryFlowRestriction"
                        :title="
                            $t('dynamicess.batteryFlowRestrictionQuestion', {
                                i18nFallback: 'Do you want to restrict the battery usage?',
                            })
                        "
                        :options="BATTERY_FLOW_OPTIONS"
                        :required="true"
                        :inline="false"
                    />
                    <VrmOptionField
                        v-if="restrictionsEnabled"
                        v-model="alwaysApplyBatteryFlowRestriction"
                        :title="
                            $t('dynamicess.whenBatteryFlowRestrictionsApply', {
                                i18nFallback: 'When do the restrictions apply?',
                            })
                        "
                        :options="BATTERY_FLOW_ALWAYS_APPLY_RESTRICTION_OPTIONS"
                        :required="true"
                        :inline="false"
                    />
                    <VrmOptionField
                        v-if="restrictionsEnabled && alwaysApplyBatteryFlowRestriction === false"
                        v-model="batteryFlowRestrictionDifferentOnWeekends"
                        :title="
                            $t('dynamicess.batteryFlowRestrictionDifferentOnWeekendsQuestion', {
                                i18nFallback:
                                    'Do the battery flow restrictions differ for weekdays and weekends?',
                            })
                        "
                        :options="BATTERY_FLOW_RESTRICTION_DIFFER_ON_WEEKENDS_OPTIONS"
                        :required="true"
                        :inline="false"
                    />
                    <fixed-schedule
                        v-if="showSchedule"
                        value-field="restrict"
                        :value-type="SCHEDULE_VALUE_TYPES.TOGGLE"
                        :first-value="true"
                        :first-value-tooltip="
                            $t('dynamicess.minOneEnabledRestrictionTooltip', {
                                i18nFallback:
                                    'A minimum of one time range needs to have restricted charge enabled',
                            })
                        "
                        :type-label="$t(`ess.restrict_${restrictionType}`)"
                        :with-weekend="batteryFlowRestrictionDifferentOnWeekends"
                        :rows-week="rowsWeek"
                        :rows-weekend="rowsWeekend"
                        :additional-validators="[batteryRestrictionsValidator]"
                        @rows-updated="setBatteryFlowRestrictions"
                        @error="setError"
                    >
                        <template v-slot:title>
                            <br />
                            <h4>
                                {{ $t(`ess.${restrictionType}_restriction_schedule`) }}
                            </h4>
                        </template>
                    </fixed-schedule>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import VrmNumberField from '@/vue/components/forms/VrmNumberField.vue';
import VrmTimeCounter from '@/vue/components/shared/VrmTimeCounter.vue';
import {
    DYNAMIC_ESS,
    DYNAMIC_ESS_FORM_FIELDS,
    DESS_CAPABILITIES,
} from '@/vue/store/modules/dynamic-ess-form.js';
import Ess from '@/vue/components/shared/device-controls/controls/Ess';
import {
    NOT_NEGATIVE_NUMBER,
    FIELD_IS_KWH,
    BATTERY_COST_TOO_HIGH,
    CANT_BE_ZERO,
} from '@/vue/constants/validation-checks';
import { ngInject } from '@/vue/decorators/angular-interop';
import InputError from '@/vue/components/forms/InputError.vue';
import VrmOptionField from '@/vue/components/forms/VrmOptionField.vue';
import {
    BATTERY_FLOW_RESTRICTIONS,
    MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL,
    DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL,
} from '@/vue/components/pages/dynamic-ess/constants';
import { CURRENCIES } from '@/vue/store/modules/currencies';
import FixedSchedule from '@/vue/components/pages/dynamic-ess/forms/FixedSchedule.vue';
import { SCHEDULE_VALUE_TYPES } from '@/vue/components/pages/dynamic-ess/constants.js';
import BatteryRestrictionsValidator from '@/vue/components/pages/dynamic-ess/validators/battery-restrictions-validator.js';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle.vue';

@Component({
    components: {
        VrmOptionField,
        InputError,
        VrmNumberField,
        Ess,
        FixedSchedule,
        VrmTimeCounter,
        VrmToggle,
    },
})
export default class Battery extends Vue {
    NOT_NEGATIVE_NUMBER = NOT_NEGATIVE_NUMBER;
    MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL = MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL;
    DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL = DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL;
    BATTERY_COST_TOO_HIGH = BATTERY_COST_TOO_HIGH(this.$store.getters[CURRENCIES.GET_ACTIVE].code);
    FIELD_IS_KWH = FIELD_IS_KWH;
    CANT_BE_ZERO = CANT_BE_ZERO;
    batteryCostChanged = false;
    BATTERY_FLOW_RESTRICTIONS = BATTERY_FLOW_RESTRICTIONS;
    SCHEDULE_VALUE_TYPES = SCHEDULE_VALUE_TYPES;
    BATTERY_FLOW_OPTIONS = [
        {
            name: this.$t('dynamicess.batteryFlowOptions.unrestricted'),
            value: BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED,
        },
        {
            name: this.$t('dynamicess.batteryFlowOptions.noExport'),
            value: BATTERY_FLOW_RESTRICTIONS.NO_EXPORT,
        },
        {
            name: this.$t('dynamicess.batteryFlowOptions.noImport'),
            value: BATTERY_FLOW_RESTRICTIONS.NO_IMPORT,
        },
    ];
    BATTERY_FLOW_ALWAYS_APPLY_RESTRICTION_OPTIONS = [
        {
            name: this.$t('dynamicess.applyBatteryFlowRestrictionOptions.always'),
            value: true,
        },
        {
            name: this.$t('dynamicess.applyBatteryFlowRestrictionOptions.atSpecificTimes'),
            value: false,
        },
    ];
    BATTERY_FLOW_RESTRICTION_DIFFER_ON_WEEKENDS_OPTIONS = [
        {
            name: this.$t('common.yes'),
            value: true,
        },
        {
            name: this.$t('common.no'),
            value: false,
        },
    ];

    batteryRestrictionsValidator = new BatteryRestrictionsValidator('restrict');

    @ngInject()
    InstallationsService;
    installation = null;
    @Prop()
    initialValues;
    changedField = new Map();

    created() {
        let fields = [
            'batteryEnergyCapacity',
            'maximumDischargePower',
            'maximumChargePower',
            'totalEstimatedBatteryCycles',
            'batteryPrice',
            'batteryCosts',
            'batteryFlowRestriction',
            'alwaysApplyBatteryFlowRestriction',
            'batteryFlowRestrictionDifferentOnWeekends',
            'batteryFlowRestrictionRowsWeek',
            'batteryFlowRestrictionRowsWeekend',
            'isPeriodicFullChargeOn',
            'periodicFullChargeInterval',
        ];
        let initialValuesAllNull = true;
        fields.forEach(value => {
            this.changedField.set(value, false);
            if (this.initialValues[value] !== null) {
                initialValuesAllNull = false;
            }
        });
        this.creating = initialValuesAllNull;
    }

    compareAndEmitEdit(field, newValue) {
        let hasChange = false;

        let isSame;
        if (
            field === 'batteryFlowRestrictionRowsWeek' ||
            field === 'batteryFlowRestrictionRowsWeekend'
        ) {
            isSame = JSON.stringify(this.initialValues[field]) === JSON.stringify(newValue);
        } else {
            isSame =
                this.initialValues[field] !== null &&
                newValue.toString() === this.initialValues[field].toString();
        }
        this.changedField.set(field, !isSame);

        this.changedField.forEach(value => {
            hasChange = hasChange || value;
        });
        if (this.creating) {
            this.$emit('creating', true);
        }
        this.$emit('change', {
            hasChange: hasChange,
            formName: 'battery',
        });
    }

    async mounted() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const installation = await this.InstallationsService.getInstallation(this.idSite);
        this.installation = installation;
        if (this.isPeriodicFullChargeOn === null) this.isPeriodicFullChargeOn = true;
    }

    get batteryFlowRestrictionsSupported() {
        return this.$store.state.dynamicEssForm.capabilities.includes(
            DESS_CAPABILITIES.batteryFlowRestrictionsSupported,
        );
    }

    get batteryEnergyCapacity() {
        return this.$store.state.dynamicEssForm.batteryEnergyCapacity;
    }

    set batteryEnergyCapacity(type) {
        this.compareAndEmitEdit('batteryEnergyCapacity', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.batteryEnergyCapacity,
        });
    }

    get dessSocDifferent() {
        return this.$store.state.dynamicEssForm.dessSocDifferent;
    }

    set dessSocDifferent(value) {
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.dessSocDifferent,
        });
    }

    get maximumDischargePower() {
        return this.$store.state.dynamicEssForm.maximumDischargePower;
    }

    set maximumDischargePower(type) {
        this.compareAndEmitEdit('maximumDischargePower', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.maximumDischargePower,
        });
    }

    get computedBatteryCycleCost() {
        if (this.batteryPrice !== null && +this.batteryPrice === 0) {
            return 0;
        }
        if (
            !this.totalEstimatedBatteryCycles ||
            !this.batteryPrice ||
            !this.batteryEnergyCapacity
        ) {
            return null;
        }
        return (
            +this.batteryPrice / (+this.totalEstimatedBatteryCycles * +this.batteryEnergyCapacity)
        );
    }

    @Watch('computedBatteryCycleCost')
    onComputedBatteryCycleCostChange() {
        if (!this.batteryCostChanged && this.computedBatteryCycleCost !== null) {
            this.batteryCosts = +this.computedBatteryCycleCost.toFixed(2);
            this.$nextTick(() => {
                this.$refs.batteryCostsRef.vrmValidate();
            });
        }
    }

    get totalEstimatedBatteryCycles() {
        return this.$store.state.dynamicEssForm.totalEstimatedBatteryCycles;
    }

    set totalEstimatedBatteryCycles(type) {
        this.compareAndEmitEdit('totalEstimatedBatteryCycles', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.totalEstimatedBatteryCycles,
        });
    }

    get batteryPrice() {
        return this.$store.state.dynamicEssForm.batteryPrice;
    }

    set batteryPrice(type) {
        this.compareAndEmitEdit('batteryPrice', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.batteryPrice,
        });
    }

    get maximumChargePower() {
        return this.$store.state.dynamicEssForm.maximumChargePower;
    }

    set maximumChargePower(type) {
        this.compareAndEmitEdit('maximumChargePower', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.maximumChargePower,
        });
    }

    get batteryCosts() {
        return this.$store.state.dynamicEssForm.batteryCosts;
    }

    set batteryCosts(type) {
        this.compareAndEmitEdit('batteryCosts', type);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: type,
            key: DYNAMIC_ESS_FORM_FIELDS.batteryCosts,
        });
    }

    get periodicFullChargeInterval() {
        return this.$store.state.dynamicEssForm.periodicFullChargeInterval;
    }

    set periodicFullChargeInterval(interval) {
        this.compareAndEmitEdit('periodicFullChargeInterval', interval);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: interval,
            key: DYNAMIC_ESS_FORM_FIELDS.periodicFullChargeInterval,
        });
    }

    get isPeriodicFullChargeOn() {
        return this.$store.state.dynamicEssForm.isPeriodicFullChargeOn;
    }

    set isPeriodicFullChargeOn(value) {
        this.compareAndEmitEdit('isPeriodicFullChargeOn', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: value,
            key: DYNAMIC_ESS_FORM_FIELDS.isPeriodicFullChargeOn,
        });
    }

    get batteryFlowRestriction() {
        return this.$store.state.dynamicEssForm.batteryFlowRestriction;
    }

    set batteryFlowRestriction(value) {
        this.compareAndEmitEdit('batteryFlowRestriction', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: value,
            key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestriction,
        });
    }

    get alwaysApplyBatteryFlowRestriction() {
        return this.$store.state.dynamicEssForm.alwaysApplyBatteryFlowRestriction;
    }

    set alwaysApplyBatteryFlowRestriction(value) {
        this.compareAndEmitEdit('alwaysApplyBatteryFlowRestriction', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: value,
            key: DYNAMIC_ESS_FORM_FIELDS.alwaysApplyBatteryFlowRestriction,
        });
    }

    get batteryFlowRestrictionDifferentOnWeekends() {
        return this.$store.state.dynamicEssForm.batteryFlowRestrictionDifferentOnWeekends;
    }

    set batteryFlowRestrictionDifferentOnWeekends(value) {
        this.compareAndEmitEdit('batteryFlowRestrictionDifferentOnWeekends', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value: value,
            key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionDifferentOnWeekends,
        });
    }

    get idSite() {
        return this.$route.params.id;
    }

    get currency() {
        return this.$store.getters[CURRENCIES.GET_ACTIVE];
    }

    get rowsWeek() {
        return this.$store.state.dynamicEssForm.batteryFlowRestrictionRowsWeek;
    }

    get rowsWeekend() {
        return this.$store.state.dynamicEssForm.batteryFlowRestrictionRowsWeekend;
    }

    get showSchedule() {
        return (
            this.restrictionsEnabled &&
            this.alwaysApplyBatteryFlowRestriction === false &&
            this.batteryFlowRestrictionDifferentOnWeekends !== null
        );
    }

    get restrictionsEnabled() {
        return (
            this.batteryFlowRestriction !== null &&
            this.batteryFlowRestriction !== BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED
        );
    }

    setError(error) {
        if (error.week) {
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: true,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionErrorRowsWeek,
            });
        } else if (error.weekend) {
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: true,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionErrorRowsWeekend,
            });
        }
    }

    setBatteryFlowRestrictions(rows) {
        if (rows.week) {
            this.compareAndEmitEdit('batteryFlowRestrictionRowsWeek', rows.week);
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: rows.week,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionRowsWeek,
            });
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: false,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionErrorRowsWeek,
            });
        } else if (rows.weekend) {
            this.compareAndEmitEdit('batteryFlowRestrictionRowsWeekend', rows.weekend);
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: rows.weekend,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionRowsWeekend,
            });
            this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
                value: false,
                key: DYNAMIC_ESS_FORM_FIELDS.batteryFlowRestrictionErrorRowsWeekend,
            });
        }
    }

    get restrictionType() {
        return this.$store.getters[DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_TYPE];
    }
}
</script>
